import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import api from "../../services/api";
import { queryKeys } from "../api/queryKeys";
import { useAppContext } from "../../contexts/AppContext";
import { CloseOrderResponse } from "../../types/api/OrderResponse";
import { CreditCardInfo } from "types/CreditCardInfo";

interface CloseOrderParams {
  codigoParcelamento?: string;
  codigoParcelamentoEspecial?: string;
  prazo?: string;
  qtdDias?: string;
  cartao?: CreditCardInfo;
}

/**
 * Monta o objeto `cartao` do `PUT /orders` no formato da adquirente do cliente.
 *
 * São dois formatos MUTUAMENTE EXCLUSIVOS, e o backend rejeita o envio dos dois juntos:
 *
 *  - SafraPay: `temporaryCardToken` + BIN + últimos 4. O número do cartão NÃO é enviado —
 *    ele foi trocado por um token no navegador (ver `services/safrapay`).
 *  - Pagar.me: `numero`, `cvv` e `validade`, como sempre foi.
 *
 * A presença do token é o discriminador porque só o fluxo SafraPay o produz. Se ele veio,
 * o cartão foi tokenizado e mandar o número junto seria expor o PAN à toa.
 */
const montarCartao = (cartao?: CreditCardInfo) => {
  if (!cartao) {
    return null;
  }

  const comumPreenchido =
    cartao.titular && cartao.numeroParcelas && cartao.bandeira;

  if (!comumPreenchido) {
    return null;
  }

  if (cartao.temporaryCardToken) {
    return {
      temporaryCardToken: cartao.temporaryCardToken,
      titular: cartao.titular,
      numeroParcelas: cartao.numeroParcelas,
      bandeira: cartao.bandeira,
      cardFirstSixDigits: cartao.cardFirstSixDigits || "",
      cardLastFourDigits: cartao.cardLastFourDigits || "",
      ...(cartao.antifraudeSessionId
        ? { antifraudeSessionId: cartao.antifraudeSessionId }
        : {}),
    };
  }

  if (!cartao.numero || !cartao.validade || !cartao.cvv) {
    return null;
  }

  return {
    numero: cartao.numero,
    titular: cartao.titular,
    validade: cartao.validade,
    cvv: cartao.cvv,
    numeroParcelas: cartao.numeroParcelas,
    bandeira: cartao.bandeira,
  };
};

export const useCloseOrder = () => {
  const queryClient = useQueryClient();
  const { nrPedidoAtivo, setNrPedidoAtivo, getCampanhaSolarValue } =
    useAppContext();

  return useMutation<CloseOrderResponse, unknown, CloseOrderParams>({
    mutationFn: async parcelamentoData => {
      const endpoint =
        nrPedidoAtivo !== null ? `orders/${nrPedidoAtivo}` : "orders";

      const cartao = montarCartao(parcelamentoData.cartao);

      const response = await api.put<CloseOrderResponse>(endpoint, {
        codigoParcelamento: parcelamentoData.codigoParcelamento || "",
        codigoParcelamentoEspecial:
          parcelamentoData.codigoParcelamentoEspecial || "",
        prazoMedio: parcelamentoData.prazo || "",
        numeroDiasVencimento: parcelamentoData.qtdDias || "",
        cartao,
      });

      if (response.status !== 200) {
        throw new Error("Não foi possível finalizar o pedido");
      }

      return response.data;
    },
    onSuccess: data => {
      // Obtém o valor atual da campanha solar do Context
      const isCampanhaSolar = getCampanhaSolarValue();

      // Trata diferentes códigos de resposta
      if (data.errorCode && parseFloat(String(data.errorCode)) === 10) {
        toast.success(
          "Seu pedido foi finalizado com sucesso! Acesse o eGAM para acompanhá-lo."
        );
        setNrPedidoAtivo(null);

        // Limpa o cache do order
        const emptyOrderData = {
          nr_pedido: "",
          items: [],
          clientInfo: {
            session: {
              bloqueio_pedido: { podeFechar: null, msgs: [] },
              cliente: { vlrMinimo: 50 },
            },
          },
        };
        queryClient.setQueryData(
          queryKeys.order(isCampanhaSolar),
          emptyOrderData
        );
        // Invalida apenas a query específica do isCampanhaSolar atual
        queryClient.invalidateQueries({
          queryKey: queryKeys.order(isCampanhaSolar),
        });
      } else if (
        data.pedidoFechado &&
        parseFloat(String(data.pedidoFechado.codigoErro)) === 0
      ) {
        toast.success(
          "Seu pedido foi finalizado com sucesso! Acesse o eGAM para acompanhá-lo."
        );
        setNrPedidoAtivo(null);

        // Limpa o cache do order
        const emptyOrderData = {
          nr_pedido: "",
          items: [],
          clientInfo: {
            session: {
              bloqueio_pedido: { podeFechar: null, msgs: [] },
              cliente: { vlrMinimo: 50 },
            },
          },
        };
        queryClient.setQueryData(
          queryKeys.order(isCampanhaSolar),
          emptyOrderData
        );
        // Invalida apenas a query específica do isCampanhaSolar atual
        queryClient.invalidateQueries({
          queryKey: queryKeys.order(isCampanhaSolar),
        });
      } else {
        // Erro ao finalizar
        toast.error("Ocorreu um erro a finalizar o pedido.");
      }
    },
    onError: () => {
      toast.error("Ocorreu um erro a finalizar o pedido.");
    },
  });
};
